import React, { useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  serverTimestamp,
  arrayRemove,
} from "firebase/firestore";

const AuthContext = React.createContext();
export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);

  // --- PURE AUTH WRAPPERS ---
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }
  function logOut() {
    return auth.signOut();
  }
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }
  async function signInWithGoogle() {
    // On délègue juste la popup, on ne touche PAS au Firestore ici
    return signInWithPopup(auth, new GoogleAuthProvider());
  }

  // --- CENTRAL PROFILE SETUP / INVITE ACCEPTANCE ---
  async function ensureProfile(user) {
    try {
      const profileRef = doc(db, "profilsUsers", user.uid);
      const profileSnap = await getDoc(profileRef);
      const isNew = !profileSnap.exists();

      const inviteQ = query(
        collection(db, "companies"),
        where("invitedEmails", "array-contains", user.email)
      );
      const inviteSnap = await getDocs(inviteQ);

      let assignedRole = inviteSnap.empty ? "Admin" : "Normal User";
      let companyId = "";
      let companyName = "";

      if (!inviteSnap.empty) {
        const compDoc = inviteSnap.docs[0];
        const data = compDoc.data();
        companyId = compDoc.id;
        companyName = data.name;
        await updateDoc(compDoc.ref, {
          memberIds: arrayUnion(user.uid),
          invitedEmails: arrayRemove(user.email),
        });
        const entry = (data.invitedUsers || []).find(
          (u) => u.email === user.email
        );
        assignedRole = entry?.role || "Normal User";
      }

      if (isNew) {
        await setDoc(profileRef, {
          language: "en",
          name: user.displayName,
          email: user.email,
          photoUrl: user.photoURL,
          role: assignedRole,
          companyId,
          companyName,
          createdAt: serverTimestamp(),
          joinedAt: serverTimestamp(),
        });
      } else if (companyId && !profileSnap.data().companyId) {
        await updateDoc(profileRef, {
          role: assignedRole,
          companyId,
          companyName,
          joinedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error("ensureProfile failed", {
        code: err?.code,
        message: err?.message,
        name: err?.name,
      });
      throw err;
    }
  }

  // --- ON AUTH STATE CHANGE ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await ensureProfile(user);
      }
      setCurrentUser(user);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logOut,
    resetPassword,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

